import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { RealtimeProvider } from "@/contexts/RealtimeContext";
import { DensityModeProvider } from "@/contexts/DensityModeContext";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import { DashboardSkeleton, OperatorSkeleton, LogisticsSkeleton, ArteSkeleton } from "@/components/shared/PageSkeletons";
import ProtectedRoute from "@/components/ProtectedRoute";
import Auth from "./pages/Auth";
import AdminPanel from "./pages/AdminPanel";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import { ChatButton } from "@/components/chat";
import { usePageVisibility } from "@/hooks/usePageVisibility";
import { Loader2 } from "lucide-react";
import { useChatSync } from '@/hooks/realtime/useChatSync';

// ═══════════════════════════════════════════════════════════════════
// LAZY LOADING DE PÁGINAS
// Melhora performance inicial carregando páginas sob demanda
// ═══════════════════════════════════════════════════════════════════
const ManagerDashboard = lazy(() => import('./pages/ManagerDashboard'));
const OperatorDashboard = lazy(() => import('./pages/OperatorDashboard'));
const LogisticsPage = lazy(() => import('./pages/LogisticsPage'));
const LogisticsHistoryPage = lazy(() => import('./pages/LogisticsHistoryPage'));
const SchedulingPage = lazy(() => import('./pages/SchedulingPage'));
const IntelligencePage = lazy(() => import('./pages/IntelligencePage'));
const RetrabalhoPage = lazy(() => import('./pages/RetrabalhoPage'));
const EstoquePage = lazy(() => import('./pages/EstoquePage'));
const EstoquistaPage = lazy(() => import('./pages/EstoquistaPage'));
const ComprasDemandasPage = lazy(() => import('./pages/ComprasDemandasPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const ArteFinalistaPage = lazy(() => import('./pages/ArteFinalistaPage'));
const ProductionPanelPage = lazy(() => import('./pages/ProductionPanelPage'));
const IntegracoesPage = lazy(() => import('./pages/Integracoes'));
const ComoFuncionaPage = lazy(() => import('./pages/ComoFunciona'));
const UsuariosPage = lazy(() => import('./pages/UsuariosPage'));
const AdminAutomationPage = lazy(() => import('./pages/AdminAutomationPage'));
const FeedbacksPage = lazy(() => import('./pages/FeedbacksPage'));
const CadastrosPage = lazy(() => import('./pages/CadastrosPage'));
const FeriadosPage = lazy(() => import('./pages/FeriadosPage'));

// HOTFIX 4 (Walter 2026-05-18 14:50 BRT): config padrão do QueryClient antes
// era `new QueryClient()` sem defaultOptions — refetchOnWindowFocus default=true
// fazia TODAS as queries da aba refetcharem a cada Alt+Tab/clique em aba.
// Com Walter+equipe alternando entre 6+ abas constantemente = ~42 req/s
// sustentado, mesmo com 0 mutations. Realtime via socket já mantém dados
// atualizados; o refetch automático em focus é redundante e tóxico em
// produção multi-aba. Esperado: tráfego cai pra ~5 req/s.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      staleTime: 30000,
      retry: 1,
    },
  },
});

// Componente de loading para Suspense
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Monitors que não precisam de realtime (já centralizado no RealtimeProvider)
const AppMonitors = () => {
  usePageVisibility();
  return null;
};

// Chat flutuante - só aparece para usuários autenticados
const AuthenticatedChat = () => {
  const { user } = useAuth();
  const { pathname, search } = useLocation();
  if (!user) return null;
  // Modo TV do painel (/painel?tv=1): o botão redondo cobriria o card da EXPEDIÇÃO.
  if (pathname === '/painel' && new URLSearchParams(search).get('tv') === '1') return null;
  return <ChatButton />;
};

// Sincronização realtime do chat - precisa estar dentro do AuthProvider
const ChatRealtimeSync = () => {
  const { user } = useAuth();
  useChatSync(); // Hook interno já verifica se user existe
  return null;
};


const App = () => {
  
  return (
    <ErrorBoundary moduleName="App">
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
          <TooltipProvider>
            <RealtimeProvider>
              <Toaster />
              <Sonner />
              <AppMonitors />
              <BrowserRouter>
                <AuthProvider>
                  {/* TenantProvider precisa estar dentro do AuthProvider (depende de useAuth) */}
                  <TenantProvider>
                  {/* DensityModeProvider DENTRO do AuthProvider para acessar userId */}
                  <DensityModeProvider>
                    <Routes>
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/admin" element={<ProtectedRoute requireSuperAdmin allowOrgAdmin><AdminPanel /></ProtectedRoute>} />
                      <Route
                        path="/integracoes"
                        element={
                          <ErrorBoundary moduleName="Integracoes">
                            <Suspense fallback={<PageLoader />}>
                              <IntegracoesPage />
                            </Suspense>
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/como-funciona"
                        element={
                          <ErrorBoundary moduleName="ComoFunciona">
                            <Suspense fallback={<PageLoader />}>
                              <ComoFuncionaPage />
                            </Suspense>
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/admin/automacao"
                        element={
                          <ErrorBoundary moduleName="AdminAutomation">
                            <Suspense fallback={<PageLoader />}>
                              <AdminAutomationPage />
                            </Suspense>
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/feedbacks"
                        element={
                          <ProtectedRoute requiredRoles={["admin", "gerente"]}>
                            <ErrorBoundary moduleName="Feedbacks">
                              <Suspense fallback={<PageLoader />}>
                                <FeedbacksPage />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/cadastros"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <ErrorBoundary moduleName="Cadastros">
                              <Suspense fallback={<PageLoader />}>
                                <CadastrosPage />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/feriados"
                        element={
                          <ProtectedRoute requiredRoles={["admin", "gerente"]}>
                            <ErrorBoundary moduleName="Feriados">
                              <Suspense fallback={<PageLoader />}>
                                <FeriadosPage />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/auth/reset-password" element={<ResetPassword />} />
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute requiredRoles={["admin", "gerente"]}>
                            <ErrorBoundary moduleName="Manager Dashboard">
                              <Suspense fallback={<DashboardSkeleton />}>
                                <ManagerDashboard />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/operador"
                        element={
                          <ProtectedRoute
                            requiredRoles={["producao"]}
                            allowedSectors={[
                              'CORTE', 'COSTURA', 'SILK', 'DTF', 'SUBLIMACAO',
                              'BORDADO', 'IMPRESSAO', 'SAIDA_ESTOQUE', 'COMPRAS', 'EXPEDICAO'
                            ]}
                          >
                            <ErrorBoundary moduleName="Operador">
                              <Suspense fallback={<OperatorSkeleton />}>
                                <OperatorDashboard />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/logistics"
                        element={
                          <ProtectedRoute requiredRoles={["admin", "gerente", "producao"]} allowedSectors={["LOGISTICA"]}>
                            <ErrorBoundary moduleName="Logistica">
                              <Suspense fallback={<LogisticsSkeleton />}>
                                <LogisticsPage />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/logistics/history"
                        element={
                          <ProtectedRoute requiredRoles={["admin", "gerente", "producao"]} allowedSectors={["LOGISTICA"]}>
                            <ErrorBoundary moduleName="Logistica Historico">
                              <Suspense fallback={<PageLoader />}>
                                <LogisticsHistoryPage />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/scheduling"
                        element={
                          <ProtectedRoute requiredRoles={["admin", "gerente"]}>
                            <ErrorBoundary moduleName="Programacao">
                              <Suspense fallback={<PageLoader />}>
                                <SchedulingPage />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/retrabalho"
                        element={
                          <ProtectedRoute requiredRoles={["admin", "gerente"]}>
                            <ErrorBoundary moduleName="Retrabalho">
                              <Suspense fallback={<PageLoader />}>
                                <RetrabalhoPage />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/estoque"
                        element={
                          /* `allowedSectors={[]}` é de propósito: sem a prop o ProtectedRoute
                             trata "nenhum setor exigido" como acesso liberado e nunca nega a
                             rota que só tem requiredRoles. Vazio = só admin/gerente entra. */
                          <ProtectedRoute requiredRoles={["admin", "gerente"]} allowedSectors={[]}>
                            <ErrorBoundary moduleName="Estoque">
                              <Suspense fallback={<PageLoader />}>
                                <EstoquePage />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/estoquista"
                        element={
                          <ProtectedRoute requiredRoles={["admin", "gerente"]} allowedSectors={["ESTOQUE"]}>
                            <ErrorBoundary moduleName="Posto de Estoque">
                              <Suspense fallback={<PageLoader />}>
                                <EstoquistaPage />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/compras-demandas"
                        element={
                          <ProtectedRoute requiredRoles={["admin", "gerente"]} allowedSectors={["COMPRAS"]}>
                            <ErrorBoundary moduleName="Compras">
                              <Suspense fallback={<PageLoader />}>
                                <ComprasDemandasPage />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/intelligence"
                        element={
                          <ProtectedRoute requiredRoles={["admin", "gerente"]}>
                            <ErrorBoundary moduleName="Intelligence">
                              <Suspense fallback={<PageLoader />}>
                                <IntelligencePage />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/relatorios"
                        element={
                          <ProtectedRoute requireSuperAdmin allowOrgAdmin>
                            <ErrorBoundary moduleName="Relatorios">
                              <Suspense fallback={<PageLoader />}>
                                <ReportsPage />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/painel"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]} allowedSectors={[]}>
                            <ErrorBoundary moduleName="Painel Producao">
                              <Suspense fallback={<PageLoader />}>
                                <ProductionPanelPage />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/arte"
                        element={
                          <ProtectedRoute requiredRoles={["admin", "gerente", "producao"]} allowedSectors={["ARTE"]}>
                            <ErrorBoundary moduleName="Arte-Finalista">
                              <Suspense fallback={<ArteSkeleton />}>
                                <ArteFinalistaPage defaultProfile="arte" />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/comercial"
                        element={
                          <ProtectedRoute requiredRoles={["admin", "gerente", "producao"]} allowedSectors={["COMERCIAL"]}>
                            <ErrorBoundary moduleName="Comercial">
                              <Suspense fallback={<PageLoader />}>
                                <ArteFinalistaPage defaultProfile="vendedor" />
                              </Suspense>
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/usuarios"
                        element={
                          <ErrorBoundary moduleName="Usuarios">
                            <Suspense fallback={<PageLoader />}>
                              <UsuariosPage />
                            </Suspense>
                          </ErrorBoundary>
                        }
                      />
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                    <ChatRealtimeSync />
                    <AuthenticatedChat />
                  </DensityModeProvider>
                  </TenantProvider>
                </AuthProvider>
              </BrowserRouter>
            </RealtimeProvider>
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
